/**
 * Announcement Service
 * 
 * Implementation matching the NOTIFICATIONS_ANNOUNCEMENTS_PLAN.md specification
 * Handles announcement creation, management, targeting, and analytics
 */

import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc,
  query, 
  where, 
  orderBy, 
  limit,
  Timestamp,
  onSnapshot,
  Unsubscribe,
  arrayUnion,
  arrayRemove,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { 
  Announcement, 
  AnnouncementData, 
  AnnouncementFilters, 
  AnnouncementView,
  AnnouncementStatus 
} from '../@types/announcement';
import { User } from '../contexts/AuthContext';
import { userService } from './UserService';
import { FileStorageService } from './FileStorageService';

export class AnnouncementService {
  private static readonly ANNOUNCEMENTS_COLLECTION = 'announcements';

  /**
   * Create a new announcement
   */
  static async createAnnouncement(
    data: AnnouncementData,
    createdByUserId: string,
    createdByName: string
  ): Promise<string> {
    try {
      // Validate required fields
      if (!data.title || !data.content) {
        throw new Error('Title and content are required');
      }

      // Clean targetAudience to remove undefined values (Firestore doesn't accept undefined)
      const cleanTargetAudience: any = {
        allUsers: data.targetAudience.allUsers || false
      };
      
      if (data.targetAudience.roles && data.targetAudience.roles.length > 0) {
        cleanTargetAudience.roles = data.targetAudience.roles;
      }
      
      if (data.targetAudience.userIds && data.targetAudience.userIds.length > 0) {
        cleanTargetAudience.userIds = data.targetAudience.userIds;
      }
      
      if (data.targetAudience.salesGroups && data.targetAudience.salesGroups.length > 0) {
        cleanTargetAudience.salesGroups = data.targetAudience.salesGroups;
      }
      
      if (data.targetAudience.provinces && data.targetAudience.provinces.length > 0) {
        cleanTargetAudience.provinces = data.targetAudience.provinces;
      }

      const announcementRef = await addDoc(collection(db, this.ANNOUNCEMENTS_COLLECTION), {
        title: data.title,
        content: data.content,
        priority: data.priority || 'MEDIUM',
        detailsLink: data.detailsLink || null,
        attachmentUrl: data.attachmentUrl || null,
        attachmentName: data.attachmentName || null,
        attachmentType: data.attachmentType || null,
        createdBy: createdByUserId,
        createdByName: createdByName,
        createdAt: Timestamp.now(),
        expiresAt: data.expiresAt ? Timestamp.fromDate(data.expiresAt instanceof Date ? data.expiresAt : data.expiresAt.toDate()) : null,
        targetAudience: cleanTargetAudience,
        status: 'ACTIVE' as AnnouncementStatus,
        views: []
      });

      return announcementRef.id;
    } catch (error: any) {
      console.error('Error creating announcement:', error);
      throw new Error(`Failed to create announcement: ${error.message}`);
    }
  }

  /**
   * Upload announcement attachment
   */
  static async uploadAttachment(
    file: File,
    announcementId: string,
    uploadedBy: string
  ): Promise<{ url: string; name: string; type: string }> {
    try {
      const fileStorageService = FileStorageService.getInstance();
      const path = `announcements/${announcementId}`;
      
      const result = await fileStorageService.uploadFile(file, path, uploadedBy);
      
      return {
        url: result.url,
        name: result.name,
        type: result.type
      };
    } catch (error: any) {
      console.error('Error uploading attachment:', error);
      throw new Error(`Failed to upload attachment: ${error.message}`);
    }
  }

  /**
   * Update an announcement
   */
  static async updateAnnouncement(
    announcementId: string,
    data: Partial<AnnouncementData>,
    updatedByUserId: string
  ): Promise<void> {
    try {
      const announcementRef = doc(db, this.ANNOUNCEMENTS_COLLECTION, announcementId);
      const updateData: any = {};

      if (data.title !== undefined) updateData.title = data.title;
      if (data.content !== undefined) updateData.content = data.content;
      if (data.priority !== undefined) updateData.priority = data.priority;
      if (data.detailsLink !== undefined) updateData.detailsLink = data.detailsLink;
      if (data.attachmentUrl !== undefined) updateData.attachmentUrl = data.attachmentUrl;
      if (data.attachmentName !== undefined) updateData.attachmentName = data.attachmentName;
      if (data.attachmentType !== undefined) updateData.attachmentType = data.attachmentType;
      if (data.expiresAt !== undefined) {
        updateData.expiresAt = data.expiresAt 
          ? Timestamp.fromDate(data.expiresAt instanceof Date ? data.expiresAt : data.expiresAt.toDate())
          : null;
      }
      if (data.targetAudience !== undefined) {
        // Clean targetAudience to remove undefined values (Firestore doesn't accept undefined)
        const cleanTargetAudience: any = {
          allUsers: data.targetAudience.allUsers || false
        };
        
        if (data.targetAudience.roles && data.targetAudience.roles.length > 0) {
          cleanTargetAudience.roles = data.targetAudience.roles;
        }
        
        if (data.targetAudience.userIds && data.targetAudience.userIds.length > 0) {
          cleanTargetAudience.userIds = data.targetAudience.userIds;
        }
        
        if (data.targetAudience.salesGroups && data.targetAudience.salesGroups.length > 0) {
          cleanTargetAudience.salesGroups = data.targetAudience.salesGroups;
        }
        
        if (data.targetAudience.provinces && data.targetAudience.provinces.length > 0) {
          cleanTargetAudience.provinces = data.targetAudience.provinces;
        }
        
        updateData.targetAudience = cleanTargetAudience;
      }

      await updateDoc(announcementRef, updateData);
    } catch (error: any) {
      console.error('Error updating announcement:', error);
      throw new Error(`Failed to update announcement: ${error.message}`);
    }
  }

  /**
   * Delete an announcement
   */
  static async deleteAnnouncement(announcementId: string): Promise<void> {
    try {
      const announcementRef = doc(db, this.ANNOUNCEMENTS_COLLECTION, announcementId);
      await deleteDoc(announcementRef);
    } catch (error: any) {
      console.error('Error deleting announcement:', error);
      throw new Error(`Failed to delete announcement: ${error.message}`);
    }
  }

  /**
   * Archive an announcement
   */
  static async archiveAnnouncement(announcementId: string): Promise<void> {
    try {
      const announcementRef = doc(db, this.ANNOUNCEMENTS_COLLECTION, announcementId);
      await updateDoc(announcementRef, {
        status: 'ARCHIVED' as AnnouncementStatus
      });
    } catch (error: any) {
      console.error('Error archiving announcement:', error);
      throw new Error(`Failed to archive announcement: ${error.message}`);
    }
  }

  /**
   * Get announcement by ID
   */
  static async getAnnouncementById(announcementId: string): Promise<Announcement | null> {
    try {
      const announcementRef = doc(db, this.ANNOUNCEMENTS_COLLECTION, announcementId);
      const announcementSnap = await getDoc(announcementRef);

      if (!announcementSnap.exists()) {
        return null;
      }

      return {
        id: announcementSnap.id,
        ...announcementSnap.data()
      } as Announcement;
    } catch (error: any) {
      console.error('Error fetching announcement:', error);
      throw new Error(`Failed to fetch announcement: ${error.message}`);
    }
  }

  /**
   * Get announcements for a specific user based on targeting
   */
  static async getUserAnnouncements(
    userId: string,
    userRole: string,
    filters?: AnnouncementFilters
  ): Promise<Announcement[]> {
    try {
      // Get user details for targeting
      const user = await userService.getUserById(userId);
      if (!user) {
        return [];
      }

      // Build query - only filter by status, no orderBy to avoid index requirement
      // Sorting and priority filtering will be done client-side
      let q = query(collection(db, this.ANNOUNCEMENTS_COLLECTION));

      // Apply status filter
      if (filters?.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        q = query(q, where('status', 'in', statuses));
      } else {
        q = query(q, where('status', '==', 'ACTIVE'));
      }

      // Query without orderBy (sort client-side to avoid index requirement)
      const snapshot = await getDocs(q);
      const announcements: Announcement[] = [];

      snapshot.docs.forEach(doc => {
        const data = doc.data();
        const announcement = {
          id: doc.id,
          ...data
        } as Announcement;

        // Check if announcement should be shown to this user
        if (this.shouldShowAnnouncement(announcement, user)) {
          // Check expiry
          if (announcement.expiresAt && announcement.expiresAt.toDate() < new Date()) {
            // Auto-expire if needed
            this.expireAnnouncement(announcement.id).catch(console.error);
            return;
          }

          announcements.push(announcement);
        }
      });

      // Sort by createdAt client-side
      announcements.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.()?.getTime() || 0;
        const bTime = b.createdAt?.toDate?.()?.getTime() || 0;
        return bTime - aTime; // Descending order
      });

      // Apply priority filter client-side if needed
      if (filters?.priority) {
        const priorities = Array.isArray(filters.priority) ? filters.priority : [filters.priority];
        return announcements.filter(announcement => priorities.includes(announcement.priority));
      }

      return announcements;
    } catch (error: any) {
      console.error('Error fetching user announcements:', error);
      
      // Handle missing index error gracefully
      if (error.code === 'failed-precondition') {
        console.warn('Firestore index may be required. Error:', error.message);
        // Return empty array instead of throwing to prevent infinite loading
        return [];
      }
      
      throw new Error(`Failed to fetch announcements: ${error.message}`);
    }
  }

  /**
   * Get all announcements (manager view)
   */
  static async getAllAnnouncements(filters?: AnnouncementFilters): Promise<Announcement[]> {
    try {
      // Build query - only filter by status, no orderBy to avoid index requirement
      // Sorting and priority filtering will be done client-side
      let q = query(collection(db, this.ANNOUNCEMENTS_COLLECTION));

      // Apply status filter - if undefined, show ACTIVE and ARCHIVED (skip EXPIRED and DRAFT)
      if (filters?.status) {
        const statuses = Array.isArray(filters.status) ? filters.status : [filters.status];
        q = query(q, where('status', 'in', statuses));
      } else {
        // Default: show ACTIVE and ARCHIVED for managers when no filter specified
        q = query(q, where('status', 'in', ['ACTIVE', 'ARCHIVED']));
      }

      // Query without orderBy (sort client-side to avoid index requirement)
      const snapshot = await getDocs(q);
      const announcements: Announcement[] = [];

      snapshot.docs.forEach(doc => {
        const announcement = {
          id: doc.id,
          ...doc.data()
        } as Announcement;

        // Check expiry
        if (announcement.expiresAt && announcement.expiresAt.toDate() < new Date()) {
          // Auto-expire if needed
          this.expireAnnouncement(announcement.id).catch(console.error);
          // Don't return here - let the status filter handle it
        }

        announcements.push(announcement);
      });

      // Sort by createdAt client-side
      announcements.sort((a, b) => {
        const aTime = a.createdAt?.toDate?.()?.getTime() || 0;
        const bTime = b.createdAt?.toDate?.()?.getTime() || 0;
        return bTime - aTime; // Descending order
      });

      // Apply priority filter client-side if needed
      if (filters?.priority) {
        const priorities = Array.isArray(filters.priority) ? filters.priority : [filters.priority];
        return announcements.filter(announcement => priorities.includes(announcement.priority));
      }

      return announcements;
    } catch (error: any) {
      console.error('Error fetching all announcements:', error);
      
      // Handle missing index error gracefully
      if (error.code === 'failed-precondition') {
        console.warn('Firestore index may be required. Error:', error.message);
        // Return empty array instead of throwing to prevent infinite loading
        return [];
      }
      
      throw new Error(`Failed to fetch announcements: ${error.message}`);
    }
  }

  /**
   * Check if announcement should be shown to user
   */
  static shouldShowAnnouncement(announcement: Announcement, user: User): boolean {
    const { targetAudience } = announcement;

    // Check if expired
    if (announcement.expiresAt && announcement.expiresAt.toDate() < new Date()) {
      return false;
    }

    // Check if archived
    if (announcement.status === 'ARCHIVED') {
      return false;
    }

    // Check if all users
    if (targetAudience.allUsers) {
      return true;
    }

    // Check by role
    if (targetAudience.roles && targetAudience.roles.includes(user.role)) {
      return true;
    }

    // Check by user ID
    if (targetAudience.userIds && targetAudience.userIds.includes(user.id)) {
      return true;
    }

    // Check by sales group
    if (targetAudience.salesGroups && user.salesGroup && 
        targetAudience.salesGroups.includes(user.salesGroup)) {
      return true;
    }

    // Note: Province/state checking would require additional user data
    // For now, we'll skip this check

    return false;
  }

  /**
   * Record announcement view
   */
  static async recordView(announcementId: string, userId: string): Promise<void> {
    try {
      const announcementRef = doc(db, this.ANNOUNCEMENTS_COLLECTION, announcementId);
      const view: AnnouncementView = {
        userId,
        viewedAt: Timestamp.now()
      };

      await updateDoc(announcementRef, {
        views: arrayUnion(view)
      });
    } catch (error: any) {
      console.error('Error recording view:', error);
      // Don't throw - view recording failure shouldn't break announcement display
    }
  }

  /**
   * Get unread announcement count for user
   */
  static async getUnreadCount(userId: string, userRole: string): Promise<number> {
    try {
      const announcements = await this.getUserAnnouncements(userId, userRole);
      
      // Count announcements that haven't been viewed by this user
      return announcements.filter(announcement => {
        return !announcement.views.some(view => view.userId === userId);
      }).length;
    } catch (error: any) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Expire an announcement
   */
  private static async expireAnnouncement(announcementId: string): Promise<void> {
    try {
      const announcementRef = doc(db, this.ANNOUNCEMENTS_COLLECTION, announcementId);
      await updateDoc(announcementRef, {
        status: 'EXPIRED' as AnnouncementStatus
      });
    } catch (error: any) {
      console.error('Error expiring announcement:', error);
    }
  }

  /**
   * Subscribe to real-time announcements for a user
   */
  static subscribeToAnnouncements(
    userId: string,
    userRole: string,
    callback: (announcements: Announcement[]) => void
  ): Unsubscribe {
    const q = query(
      collection(db, this.ANNOUNCEMENTS_COLLECTION),
      where('status', '==', 'ACTIVE'),
      orderBy('createdAt', 'desc')
    );

    return onSnapshot(q, async (snapshot) => {
      try {
        const user = await userService.getUserById(userId);
        if (!user) {
          callback([]);
          return;
        }

        const announcements: Announcement[] = [];

        snapshot.docs.forEach(doc => {
          const announcement = {
            id: doc.id,
            ...doc.data()
          } as Announcement;

          // Check if announcement should be shown to this user
          if (this.shouldShowAnnouncement(announcement, user)) {
            // Check expiry
            if (announcement.expiresAt && announcement.expiresAt.toDate() < new Date()) {
              this.expireAnnouncement(announcement.id).catch(console.error);
              return;
            }

            announcements.push(announcement);
          }
        });

        callback(announcements);
      } catch (error: any) {
        console.error('Error in announcement subscription:', error);
        callback([]);
      }
    });
  }
}

